import { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { ContractorHealthBadge } from "@/components/admin/ContractorHealthCard";

// Per-name in-memory cache to avoid re-fetching on every render across pages.
const cache = new Map();
const pending = new Map();

async function loadContractor(name) {
  const key = name.toLowerCase().trim();
  if (cache.has(key)) return cache.get(key);
  if (pending.has(key)) return pending.get(key);
  const p = adminApi
    .get(`/contractors/by-name/${encodeURIComponent(name)}`)
    .then(({ data }) => {
      cache.set(key, data);
      pending.delete(key);
      return data;
    })
    .catch((err) => {
      pending.delete(key);
      throw err;
    });
  pending.set(key, p);
  return p;
}

/** Self-fetching contractor health badge. Pass `name`; it will auto-create the
 *  contractor record on first sight (so the badge is always present, just UNRATED). */
export default function ContractorBadgeForName({ name, size = "sm", onClick }) {
  const [contractor, setContractor] = useState(null);
  useEffect(() => {
    if (!name) return;
    let alive = true;
    loadContractor(name)
      .then((c) => { if (alive) setContractor(c); })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, [name]);

  if (!name) return null;
  if (!contractor) {
    return <span className="inline-block bg-[#E2DCD0]/50 rounded-full px-2 py-0.5 text-[10px] text-[#94A3B8] tracking-[0.06em] uppercase">Rating…</span>;
  }
  const inner = <ContractorHealthBadge contractor={contractor} size={size}/>;
  if (onClick) {
    return (
      <button type="button" onClick={() => onClick(contractor)} className="appearance-none hover:opacity-80 transition" data-testid={`contractor-badge-btn-${contractor.id}`}>
        {inner}
      </button>
    );
  }
  return inner;
}

/** Imperative cache-clear, called after edits so badges across the page refresh. */
export function invalidateContractorHealth(name) {
  if (!name) return;
  cache.delete(name.toLowerCase().trim());
}
